import { useFormik } from 'formik';
import { Button, Dialog, InputLabel, Stack, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Company, MerchantBankAccount, useBankByRoutingNumberLazyQuery, useMerchantBankAccountsLazyQuery } from '../../graphql/types';
import { PlusOutlined } from '@ant-design/icons';
import AddMerchantBankAccount from './AddMerchantBankAccount';
import { Values } from './AddMerchantCashAdvance';
import { RecurrencyValues } from './AddRecurrence';
import { formatAccountNumber } from '../../components/ag-grid/AgGridAccountCellRenderer';
import BankInfoCell, { BankInfoCellData } from '../../components/ag-grid/BankInfoCell';

export interface Props {
  isCreating: boolean;
  merchant: Company;
  formik: ReturnType<typeof useFormik<Values> | typeof useFormik<RecurrencyValues>>;
  routingNumber?: string | undefined;
  accountNumber?: string | undefined;
}

export const MerchantBankAccountInput = ({ isCreating, merchant, formik, routingNumber, accountNumber }: Props) => {
  const { getFieldProps, setFieldValue, values, touched, errors } = formik;
  const [fetchBankAccounts] = useMerchantBankAccountsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchBankByRoutingNumber] = useBankByRoutingNumberLazyQuery({ fetchPolicy: 'network-only' });

  const [bankAccounts, setBankAccounts] = useState<readonly MerchantBankAccount[]>([]);
  const [refetchBankAccount, setRefetchBankAccount] = useState<boolean>(false);
  const [bankAccountIndex, setBankAccountIndex] = useState(-1);
  const [bankInfoData, setBankInfoData] = useState<BankInfoCellData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (merchant && merchant.id) {
        const res = await fetchBankAccounts({
          variables: {
            merchantId: merchant.id
          }
        });
        setBankAccounts((res.data?.merchantBankAccounts as MerchantBankAccount[]) || []);
      } else {
        setBankAccounts([]);
        setBankInfoData(null);
      }
    };
    fetchData().catch(console.error);
  }, [merchant, refetchBankAccount]);

  useEffect(() => {
    if (!bankAccounts.length) {
      setBankAccountIndex(-1);
      setFieldValue('accountNumber', '');
      setFieldValue('routingNumber', '');
      return;
    }
    if (accountNumber) {
      const initialIndex = bankAccounts.findIndex((account) => account.accountNumber === accountNumber);
      if (initialIndex !== -1) {
        setBankAccountIndex(initialIndex);
        setFieldValue('accountNumber', bankAccounts[initialIndex].accountNumber);
        setFieldValue('routingNumber', bankAccounts[initialIndex].routingNumber);
        return;
      }
    }
    setBankAccountIndex(0);
    setFieldValue('accountNumber', bankAccounts[0].accountNumber);
    setFieldValue('routingNumber', bankAccounts[0].routingNumber);
  }, [bankAccounts]);

  useEffect(() => {
    const rn = (values as Values).routingNumber;
    if (!rn) {
      setBankInfoData(null);
      return;
    }
    fetchBankByRoutingNumber({ variables: { routingNumber: rn } }).then((res) => {
      const bank = res.data?.bankByRoutingNumber;
      setBankInfoData(bank ? { bankName: bank.bankName, bankLogo: bank.logo, locationName: bank?.locationName } : null);
    });
  }, [(values as Values).routingNumber]);

  const handleChange = (event: any) => {
    const index = parseInt(event.target.value);
    setBankAccountIndex(index);
    setFieldValue('accountNumber', bankAccounts[index]?.accountNumber || '');
    setFieldValue('routingNumber', bankAccounts[index]?.routingNumber || '');
  };

  const [add, setAdd] = useState<boolean>(false);
  const handleAdd = () => {
    setAdd(!add);
  };

  return (
    <>
      {isCreating && merchant?.id && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
          <TextField fullWidth value={bankAccountIndex} onChange={handleChange} select SelectProps={{ native: true }} inputProps={{ style: { fontSize: 12 } }}>
            {<option value={-1}>Select Account</option>}
            {bankAccounts.map((bankAccount, index) => (
              <option key={index} value={index}>
                {bankAccount.name ? bankAccount.name : bankAccount.routingNumber} {formatAccountNumber(bankAccount.accountNumber)}
              </option>
            ))}
          </TextField>
          <Button size="small" variant="contained" sx={{ minWidth: 'max-content', p: 1.5 }} onClick={handleAdd}>
            <PlusOutlined />
          </Button>
        </Stack>
      )}
      <Stack direction="column" spacing={1.25} pt={0}>
        <Stack spacing={0.5}>
          <InputLabel required={true} htmlFor="mac-entity-routingNumber">
            Routing Number
          </InputLabel>
          <TextField
            disabled={true}
            size="small"
            fullWidth
            id="mac-entity-routingNumber"
            placeholder="Enter Routing Number"
            {...getFieldProps('routingNumber')}
            error={Boolean(touched.routingNumber && errors.routingNumber)}
            helperText={touched.routingNumber && errors.routingNumber}
          />
        </Stack>
        <Stack spacing={0.5}>
          <InputLabel required={true} htmlFor="mac-entity-accountNumber">
            Account Number
          </InputLabel>
          <TextField
            disabled={true}
            size="small"
            fullWidth
            id="mac-entity-accountNumber"
            placeholder="Enter Account Number"
            {...getFieldProps('accountNumber')}
            error={Boolean(touched.accountNumber && errors.accountNumber)}
            helperText={touched.accountNumber && errors.accountNumber}
          />
        </Stack>
        <BankInfoCell data={bankInfoData} showUnknown={!bankInfoData && (values.routingNumber?.length || 0) === 9} />
      </Stack>
      {/* add bank account dialog */}
      <Dialog maxWidth="sm" fullWidth onClose={handleAdd} open={add} sx={{ '& .MuiDialog-paper': { p: 0, maxWidth: 550 }}}>
        {add && (
          <AddMerchantBankAccount
            merchant={merchant}
            entity={{ accountNumber, routingNumber }}
            onCancel={handleAdd}
            onSave={(bankAccount) => {
              setFieldValue('accountNumber', bankAccount?.accountNumber);
              setFieldValue('routingNumber', bankAccount?.routingNumber);
              setRefetchBankAccount(!refetchBankAccount);
              setAdd(!add);
            }}
          />
        )}
      </Dialog>
    </>
  );
};
